import { UseToastOptions } from "@chakra-ui/react";
import { SyncWalletErrorDetailCode } from "@syla/shared/types/models/WalletBase";
import {
  SyncWalletResponse,
  WalletSyncFailed,
} from "@syla/shared/types/responses/SyncWalletResponse";
import React from "react";
import { parseSyncErrorMessage } from "../../api/errors/parsers/parseSyncErrorMessage";
import { errorToastOptions } from "../../components/molecules/errorToastOptions";
import { MarkdocContent } from "../../markdoc/MarkdocContent";

export function buildSyncErrorToastProps(
  response: SyncWalletResponse,
): UseToastOptions {
  const { title, status, cause, message } = getSyncWalletErrorProps(response);

  const text = cause ? `${cause}: ${message}` : message;

  const description = <MarkdocContent content={text} />;

  return {
    ...errorToastOptions,
    status,
    title,
    description,
  };
}

function getSyncWalletErrorProps(response: SyncWalletResponse): {
  status: "info" | "error";
  title: string;
  message: string;
  cause?: string;
} {
  // only a single wallet failed, we can be more specific with the error message
  if (response.failed.length === 1) {
    const failedSync = response.failed[0];
    const props = parseSyncErrorMessage(
      failedSync.syncAuthType,
      failedSync.cause,
      failedSync.dataSourceType,
    );
    return {
      status: isInfoLevelFailure(failedSync) ? "info" : "error",
      title: `Couldn't sync ${failedSync.walletCustomName || failedSync.dataSourceName}`,
      cause: props.title,
      message: props.message,
    };
  }

  // multiple wallets failed.
  else {
    const isInfo = response.failed.every(isInfoLevelFailure);
    const message =
      "Please try again later. If the problem continues, contact support for assistance.";

    return {
      status: isInfo ? "info" : "error",
      title: `Couldn't sync some Data Sources`,
      message,
    };
  }
}

function isInfoLevelFailure(failed: WalletSyncFailed): boolean {
  return [
    SyncWalletErrorDetailCode.RATE_LIMIT,
    SyncWalletErrorDetailCode.CUSTOM,
  ].includes(failed.cause.code);
}
